/**
 * نمودار تریدینگ‌ویو (اختیاری)
 * ==============================
 * اگر کتابخانه نموداری تریدینگ‌ویو روی سایت نصب شده باشد، این کامپوننت
 * همان نمودارِ کاملی را می‌آورد که صرافی‌های بزرگ — از جمله نوبیتکس —
 * استفاده می‌کنند: بیش از صد اندیکاتور، ده‌ها ابزار ترسیم، الگوهای هارمونیک
 * و الیوت، ذخیره چیدمان، و بقیه چیزهایی که تیم تریدینگ‌ویو سال‌ها رویشان
 * کار کرده است.
 *
 * **داده از سرور خودمان می‌آید، نه از تریدینگ‌ویو.** این نکته برای صرافی
 * حیاتی است: قیمت تومانیِ این سایت حاشیه خودش را دارد و اگر نمودار از
 * سرور دیگری می‌آمد، عددِ نمودار با دکمه «خرید» نمی‌خواند.
 *
 * اگر کتابخانه نصب نباشد، این کامپوننت **هیچ کاری نمی‌کند** و نمودار خودمان
 * سر جایش می‌ماند. روش نصب در `docs/tradingview.md` نوشته شده است.
 */
import { useEffect, useRef, useState } from 'react'
import { createDatafeed } from '../lib/tvDatafeed'

export const TV_LIB = '/charting_library/charting_library.js'

/** آیا کتابخانه روی سرور هست؟ نتیجه یک بار گرفته و نگه داشته می‌شود. */
let cached: Promise<boolean> | null = null
export function tvAvailable(): Promise<boolean> {
  if (!cached) {
    cached = fetch(TV_LIB, { method: 'HEAD' })
      .then((r) => r.ok && !String(r.headers.get('content-type') || '').includes('text/html'))
      .catch(() => false)
  }
  return cached
}

let loading: Promise<boolean> | null = null
function loadLib(): Promise<boolean> {
  if ((window as any).TradingView?.widget) return Promise.resolve(true)
  if (!loading) {
    loading = new Promise<boolean>((resolve) => {
      const s = document.createElement('script')
      s.src = TV_LIB
      s.async = true
      s.onload = () => resolve(!!(window as any).TradingView?.widget)
      s.onerror = () => resolve(false)
      document.head.appendChild(s)
    })
  }
  return loading
}

type Props = { symbol: string; interval?: string; onFail?: () => void }

export function TradingViewChart({ symbol, interval = '60', onFail }: Props) {
  const box = useRef<HTMLDivElement>(null)
  const widget = useRef<any>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    loadLib().then((ok) => {
      if (!alive) return
      if (!ok || !box.current) {
        setErr('کتابخانه نمودار پیشرفته روی سرور نصب نیست')
        onFail?.()
        return
      }
      try {
        widget.current = new (window as any).TradingView.widget({
          container: box.current,
          library_path: '/charting_library/',
          datafeed: createDatafeed(),
          symbol,
          interval,
          locale: 'fa',
          timezone: 'Asia/Tehran',
          theme: 'dark',
          autosize: true,
          debug: false,
          enabled_features: ['study_templates'],
          disabled_features: ['use_localstorage_for_settings'],
          overrides: {
            'paneProperties.background': '#0B0D14',
            'paneProperties.backgroundType': 'solid',
            'paneProperties.vertGridProperties.color': 'rgba(255,255,255,.04)',
            'paneProperties.horzGridProperties.color': 'rgba(255,255,255,.04)',
            'scalesProperties.textColor': '#8B93AD',
            'mainSeriesProperties.candleStyle.upColor': '#00E39A',
            'mainSeriesProperties.candleStyle.downColor': '#FF4D6D',
            'mainSeriesProperties.candleStyle.wickUpColor': '#00E39A',
            'mainSeriesProperties.candleStyle.wickDownColor': '#FF4D6D',
            'mainSeriesProperties.candleStyle.borderUpColor': '#00E39A',
            'mainSeriesProperties.candleStyle.borderDownColor': '#FF4D6D',
          },
        })
      } catch (e: any) {
        setErr(e?.message || 'اجرای نمودار پیشرفته ناموفق بود')
        onFail?.()
      }
    })
    return () => {
      alive = false
      // بدون این، هر بار عوض‌کردن بازار یک نمودار سنگین در حافظه جا می‌گذارد
      try { widget.current?.remove?.() } catch { /* از قبل بسته شده */ }
      widget.current = null
    }
  }, [symbol, interval, onFail])

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={box} className="absolute inset-0" />
      {err && (
        <div className="absolute inset-0 grid place-items-center p-4 text-center">
          <div className="max-w-sm rounded-xl border border-white/10 bg-ink-900/85 px-4 py-3 text-xs leading-6 text-slate-400">
            {err}
            <div className="mt-1 text-[11px] text-slate-500">
              راهنمای نصب: <span className="num" dir="ltr">docs/tradingview.md</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
